import { cn } from '@rocco/utils/cn';

export type SecureSignInGlyphState = 'idle' | 'busy' | 'success';

/**
 * Face ID scan motion, hand-drawn: the design frame (Figma 3162:14229)
 * embeds a screen recording of the Apple animation as its reference — a
 * video asset would be heavy, fuzzy at this size, and Apple's rendering,
 * so this recreates it as a vector. The face looks around inside the
 * frame corners while a ceremony runs, and a passed verify fades the face
 * into a drawn checkmark. Transforms, opacity and stroke offsets only, so
 * it stays on the compositor; prefers-reduced-motion gets the static
 * states without motion.
 */
const FACEID_CSS = `
.ap-faceid-face, .ap-faceid-brackets {
    transform-box: fill-box;
    transform-origin: center;
}
.ap-faceid-check {
    opacity: 0;
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
}
@media (prefers-reduced-motion: no-preference) {
    .ap-faceid--busy .ap-faceid-face {
        animation: ap-faceid-look 2.4s ease-in-out infinite;
    }
    .ap-faceid--busy .ap-faceid-brackets {
        animation: ap-faceid-breathe 2.4s ease-in-out infinite;
    }
    .ap-faceid--success .ap-faceid-face {
        animation: ap-faceid-face-out 200ms ease-in forwards;
    }
    .ap-faceid--success .ap-faceid-check {
        animation: ap-faceid-check-in 400ms ease-out 150ms forwards;
    }
    .ap-faceid--success .ap-faceid-brackets {
        animation: ap-faceid-pop 450ms ease-out 250ms;
    }
}
@media (prefers-reduced-motion: reduce) {
    .ap-faceid--success .ap-faceid-face {
        opacity: 0;
    }
    .ap-faceid--success .ap-faceid-check {
        opacity: 1;
        stroke-dashoffset: 0;
    }
}
@keyframes ap-faceid-look {
    0%, 6% { transform: translate(0, 0) rotate(0deg); }
    16%, 26% { transform: translate(-0.375rem, -0.125rem) rotate(-6deg); }
    38%, 48% { transform: translate(0.375rem, -0.125rem) rotate(6deg); }
    60%, 68% { transform: translate(0, 0.25rem) rotate(0deg); }
    80%, 100% { transform: translate(0, 0) rotate(0deg); }
}
@keyframes ap-faceid-breathe {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(0.955); }
}
@keyframes ap-faceid-face-out {
    to { opacity: 0; transform: scale(0.85); }
}
@keyframes ap-faceid-check-in {
    from { opacity: 1; stroke-dashoffset: 1; }
    to { opacity: 1; stroke-dashoffset: 0; }
}
@keyframes ap-faceid-pop {
    0% { transform: scale(1); }
    40% { transform: scale(1.05); }
    100% { transform: scale(1); }
}
`;

/**
 * The Secure Sign-In face glyph shared by the enrol ask and the step-0
 * login. Idle it sits dimmed and still; while a ceremony runs it plays
 * the Face ID-style scan; a passed verify draws the checkmark.
 */
export const SecureSignInGlyph = ({
    state,
}: {
    state: SecureSignInGlyphState;
}) => (
    <div className="flex justify-center">
        <svg
            viewBox="0 0 96 96"
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className={cn(
                'ap-faceid size-29.5 text-[#181818] transition-opacity duration-300',
                state === 'idle' && 'opacity-50',
                state === 'busy' && 'ap-faceid--busy',
                state === 'success' && 'ap-faceid--success',
            )}
        >
            <style>{FACEID_CSS}</style>
            <g className="ap-faceid-brackets">
                <path d="M31 8 H22 C14.3 8 8 14.3 8 22 V31" />
                <path d="M65 8 H74 C81.7 8 88 14.3 88 22 V31" />
                <path d="M88 65 V74 C88 81.7 81.7 88 74 88 H65" />
                <path d="M8 65 V74 C8 81.7 14.3 88 22 88 H31" />
            </g>
            <g className="ap-faceid-face">
                <path d="M33 36 V46" />
                <path d="M63 36 V46" />
                <path d="M48 36 V50 C48 53 46 54.5 43.5 54.5" />
                <path d="M33 65 C37 70.5 42.3 73.5 48 73.5 C53.7 73.5 59 70.5 63 65" />
            </g>
            <path
                className="ap-faceid-check"
                d="M32 50 L44 62 L66 38"
                pathLength={1}
            />
        </svg>
    </div>
);
